import React, { FC } from 'react';
import { Typography, Space, Select, Button, Spin, Row, Col, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import cx from 'classnames';
import { NamePath } from 'antd/lib/form/interface';
import { isEqual } from 'lodash';

import { useBusinessDocumentType } from '~/hooks/useHandbook';
import { useCurrentBusiness } from '~/hooks/businesses';
import {
  Form,
  BusinessDocumentParametersInputGroup,
  BusinessDocumentPagesInputGroup,
  BackButton,
} from '~/components';
import { useBusinessAdditionalDocumentsUpdate, useKycPage } from '~/hooks/kyc';
import { useStatefulForm } from '~/hooks/form';
import { formWidth } from './utils';

const { Title, Text } = Typography;

const AdditionalDocuments: FC<any> = (props) => {
  const { className } = props;

  const [t] = useTranslation('kyc');
  const currentBusiness = useCurrentBusiness();
  const currentAdditionalDocuments = currentBusiness.data?.documents;
  const countryId = currentBusiness.data?.country;
  const legalType = currentBusiness.data?.legalType;
  const businessDocumentType = useBusinessDocumentType({
    countryId,
    legalType,
    filterOptions: (item) => !item.primary,
  });

  type AdditionalDocumentsType = {
    documents: typeof currentAdditionalDocuments;
  };

  const { form, handleFormChange, formState } = useStatefulForm<AdditionalDocumentsType>(
    currentAdditionalDocuments ? { documents: currentAdditionalDocuments } : undefined
  );

  const additionalDocumentsMutation = useBusinessAdditionalDocumentsUpdate();
  const kycPage = useKycPage();
  const onFinish = async (values: AdditionalDocumentsType) => {
    if (additionalDocumentsMutation.ready) {
      if (!isEqual(values.documents, currentBusiness.data?.documents)) {
        await additionalDocumentsMutation.mutateAsync(values.documents);
      }
      kycPage.goNext();
    }
  };

  return (
    <Space direction="vertical" size="large" className={cx(className, 'Step-Container')}>
      <div>
        <Title level={3}>{t('Additional company documents')}</Title>
        <Text>
          {t('Select type of submitted information and click to upload document scan(s)')}
        </Text>
      </div>
      {!currentBusiness.data ? (
        <Spin />
      ) : (
        <Form
          form={form}
          labelAlign="right"
          onValuesChange={(_, newValues) => handleFormChange(newValues)}
          onFinish={onFinish}
          layout="vertical"
          scrollToFirstError
        >
          <Form.List name="documents">
            {(fields, { add, remove }) => (
              <>
                <Space direction="vertical" size={40}>
                  {fields.map((field) => {
                    const getNamePath = (name: NamePath) => [field.name, name].flat();
                    const documentType = formState?.documents?.[field.name]?.type;
                    return (
                      <div key={`additionalDocuments-${field.fieldKey}`}>
                        <Title level={4}>
                          {businessDocumentType.options?.find((item) => item.value === documentType)
                            ?.label || t('New document')}
                        </Title>
                        <Row className="AdditionalDocuments-DocumentForm" gutter={[rowGap, 0]}>
                          <Col flex="none" style={{ width: formWidth.width + rowGap }}>
                            <Form.Item
                              label={t('Document type')}
                              name={getNamePath('type')}
                              rules={[{ required: true }]}
                            >
                              <Select
                                options={businessDocumentType.options}
                                loading={businessDocumentType.isLoading}
                              />
                            </Form.Item>
                            <BusinessDocumentParametersInputGroup
                              nameFn={getNamePath}
                              countryId={countryId}
                              legalType={legalType}
                              documentType={documentType}
                            />
                          </Col>
                          <Col flex="none">
                            <BusinessDocumentPagesInputGroup
                              nameFn={(fileIndex) => getNamePath(['files', fileIndex])}
                              countryId={countryId}
                              legalType={legalType}
                              documentType={documentType}
                            />
                          </Col>
                          <Col flex="none">
                            <Tooltip title={t('Delete document')}>
                              <MinusCircleOutlined
                                className="AdditionalDocuments-RemoveButton"
                                onClick={() => remove(field.name)}
                              />
                            </Tooltip>
                          </Col>
                        </Row>
                      </div>
                    );
                  })}
                </Space>
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    style={formWidth}
                  >
                    {t('Add document')}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Row gutter={16}>
            <Col>
              <BackButton onClick={kycPage.goBack} size="large" />
            </Col>
            <Col>
              <Form.Item noStyle>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  loading={additionalDocumentsMutation.isLoading}
                >
                  {t('translation:Next')}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Space>
  );
};

const rowGap = 16;

export default styled(AdditionalDocuments)`
  .AdditionalDocuments-DocumentForm {
    // margin-bottom: 40px;
  }
  .ant-row {
    flex-wrap: nowrap;
  }
`;
